import { Controller } from "@hotwired/stimulus"

// The "arrange this home" modal, opened by the pencil beside the widget strip's
// autoplay ring. The section and widget forms save in the background through
// the shared autosave controller, so this controller only owns visibility.
// There is no "apply" step to manage here — closing is just closing.
export default class extends Controller {
    static targets = ["modal", "main", "picker"]

    connect() {
        this._onKeydown = (event) => { if (event.key === "Escape") this.close() }
        document.addEventListener("keydown", this._onKeydown)
    }

    disconnect() {
        document.removeEventListener("keydown", this._onKeydown)
    }

    open(event) {
        event?.preventDefault()
        if (!this.hasModalTarget) return

        this.modalTarget.classList.remove("hidden")
        // Focus the dialog so Escape and tabbing land inside it rather than on
        // whatever was behind.
        this.modalTarget.querySelector("[role=dialog]")?.focus?.()
    }

    // "Edit widgets" in the dashboard picker: a desktop home's edit mode lives
    // on its own controller, below the header, so tell it by event.
    editWidgets(event) {
        event?.preventDefault()
        window.dispatchEvent(new CustomEvent("home-desktop:edit"))
    }

    // Quick actions' picker: a view of its own in place of the settings, like a
    // pin folder's list. Its frame is lazy, so showing it is what loads it.
    showPicker(event) {
        event?.preventDefault()
        if (!this.hasPickerTarget) return
        this.mainTarget.hidden = true
        this.pickerTarget.hidden = false
        this.pickerTarget.scrollTop = 0
        this.pickerTarget.querySelector("input[type=search]")?.focus()
    }

    // Back to the settings, whose list of quick actions reloads if the picker
    // changed it.
    hidePicker(event) {
        event?.preventDefault()
        if (!this.hasPickerTarget || this.pickerTarget.hidden) return
        this.pickerTarget.hidden = true
        this.mainTarget.hidden = false
        if (this._pickerChanged) {
            this._pickerChanged = false
            const list = this.mainTarget.querySelector("turbo-frame#home_actions_editor")
            if (list?.dataset.reloadSrc) list.src = list.dataset.reloadSrc
        }
    }

    close() {
        this.hidePicker()
        if (this.hasModalTarget) this.modalTarget.classList.add("hidden")
        // Quick actions change in place inside the modal (their own frame);
        // the home's row of chips, and on a grid the size of Jane's card,
        // catch up once, on the way out.
        if (this._actionsChanged) {
            this._actionsChanged = false
            if (window.Turbo) window.Turbo.visit(window.location.href, { action: "replace" })
            else window.location.reload()
        }
    }

    // A quick action added or taken off, from either frame. The picker's first
    // load is not a change.
    actionsChanged(event) {
        const frame = event?.target
        if (frame?.id === "home_actions_picker" && !frame.dataset.loadedOnce) {
            frame.dataset.loadedOnce = "true"
            return
        }
        this._actionsChanged = true
        if (frame?.id === "home_actions_picker") this._pickerChanged = true
    }

    // Only a click on the backdrop itself closes — a click that started inside
    // the dialog and drifted out should not.
    backdropClose(event) {
        if (event.target === this.modalTarget) this.close()
    }
}
